import type { JumpChart } from '../jumpChart.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, parseVariantSlug } from '../gameTypes.js';
import { ON_AND_ON_JUMP_VARIANTS } from './01-on-and-on.js';
import { SKY_HIGH_JUMP_VARIANTS } from './02-sky-high.js';
import { HEROES_JUMP_VARIANTS } from './03-heroes.js';
import { CRADLES_JUMP_VARIANTS } from './04-cradles.js';
import { MY_HEART_JUMP_VARIANTS } from './05-my-heart.js';
import { INVINCIBLE_JUMP_VARIANTS } from './06-invincible.js';
import { HOPE_JUMP_VARIANTS } from './07-hope.js';
import { ADVENTURE_JUMP_VARIANTS } from './08-adventure.js';
import { BLANK_JUMP_VARIANTS } from './09-blank.js';
import { DIAMOND_JUMP_VARIANTS } from './10-diamond.js';
import { HOLLAH_JUMP_VARIANTS } from './11-hollah.js';
import { NEKOZILLA_JUMP_VARIANTS } from './12-nekozilla.js';

/**
 * One playable Jump map: a single (track, variant) pair. Mirrors the other
 * modes' builtin shape so the seeder, home page, and play page iterate
 * uniformly. Like Waves, Jump ships no deterministic expected replay — the
 * server validates submitted replays via
 * {@link import('../engine/jump/headlessRunner.js').runJumpReplay}.
 */
export interface JumpBuiltinMap {
  chart: JumpChart;
  expectedReplay: null;
}

export interface JumpBuiltinTrack {
  baseSlug: string;
  variants: Record<Variant, JumpBuiltinMap>;
}

const VARIANT_BUNDLES: ReadonlyArray<{
  charts: Record<Variant, JumpChart>;
}> = [
  // Order intentionally matches the Circles / Waves / Guitar catalogue so the
  // home page (and tier 1..5 progression labels) line up across modes.
  { charts: ON_AND_ON_JUMP_VARIANTS },
  { charts: HOPE_JUMP_VARIANTS },
  { charts: ADVENTURE_JUMP_VARIANTS },
  { charts: SKY_HIGH_JUMP_VARIANTS },
  { charts: CRADLES_JUMP_VARIANTS },
  { charts: DIAMOND_JUMP_VARIANTS },
  { charts: BLANK_JUMP_VARIANTS },
  { charts: HEROES_JUMP_VARIANTS },
  { charts: HOLLAH_JUMP_VARIANTS },
  { charts: MY_HEART_JUMP_VARIANTS },
  { charts: NEKOZILLA_JUMP_VARIANTS },
  { charts: INVINCIBLE_JUMP_VARIANTS },
];

export const BUILTIN_JUMP_TRACKS: JumpBuiltinTrack[] = VARIANT_BUNDLES.map((bundle) => ({
  baseSlug: bundle.charts.normal.slug,
  variants: Object.fromEntries(
    VARIANTS.map((v) => [
      v,
      {
        chart: bundle.charts[v],
        expectedReplay: null,
      } satisfies JumpBuiltinMap,
    ]),
  ) as Record<Variant, JumpBuiltinMap>,
}));

export const BUILTIN_JUMP_MAPS: JumpBuiltinMap[] = BUILTIN_JUMP_TRACKS.flatMap((track) =>
  VARIANTS.map((v) => track.variants[v]),
);

/**
 * Look up a Jump builtin by slug (`'invincible-flow'`, `'invincible-flow-hard'`,
 * `'invincible-flow-extreme'`). Literal slug matches first, then a variant-suffix
 * parse, so a hypothetical track whose slug ends in `-hard` won't be
 * misparsed.
 */
export function getBuiltinJumpMapBySlug(slug: string): JumpBuiltinMap | undefined {
  const literalTrack = BUILTIN_JUMP_TRACKS.find((t) => t.baseSlug === slug);
  if (literalTrack) {
    return literalTrack.variants.normal;
  }
  const { baseSlug, variant } = parseVariantSlug(slug);
  const track = BUILTIN_JUMP_TRACKS.find((t) => t.baseSlug === baseSlug);
  if (!track) {
    return undefined;
  }
  return track.variants[variant];
}

export function listBuiltinJumpTracks(): JumpBuiltinTrack[] {
  return BUILTIN_JUMP_TRACKS;
}

export function listBuiltinJumpMaps(): JumpBuiltinMap[] {
  return BUILTIN_JUMP_MAPS;
}
